<template>
	<v-dialog
		:model-value="props.modelValue"
		@update:model-value="emit('update:modelValue', $event)"
		persistent
		max-width="420"
		content-class="quote-generate-modal"
		data-cy="quote-generate-modal"
		transition="dialog-transition"
	>
		<v-card class="d-flex flex-column">
			<v-card-title class="d-flex align-center justify-space-between pa-3 text-subtitle-1">
				<span>Generate Quote</span>
				<v-btn icon variant="text" size="small" @click="close" data-cy="close-quote-generate-btn">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-divider />

			<v-card-text class="pa-3 pt-2">
				<template v-if="trial">
					<div class="mb-3 text-body-2 text-medium-emphasis">
						Quote for: <strong>{{ trial.school_name }}</strong> ({{ trial.trial_id }})
					</div>
					<v-form ref="formRef" v-model="valid">
						<v-row dense>
							<v-col cols="6">
								<v-text-field
									v-model.number="form.duration"
									label="Duration (years)*"
									type="number"
									min="1"
									:rules="[rules.required, rules.minDuration]"
									density="compact"
									data-cy="quote-duration"
								/>
							</v-col>
							<v-col cols="6">
								<v-text-field
									v-model.number="form.discount"
									label="Discount ($)"
									type="number"
									min="0"
									step="0.01"
									:rules="[rules.discountNonNegative]"
									placeholder="Optional"
									density="compact"
									data-cy="quote-discount"
								/>
							</v-col>
							<v-col cols="12">
								<v-checkbox
									v-model="form.blackbaud"
									label="Include Blackbaud add-on"
									hide-details
									density="compact"
									data-cy="quote-blackbaud"
								/>
							</v-col>
							<v-col cols="12">
								<v-textarea
									v-model="form.notes"
									label="Notes"
									rows="2"
									:counter="500"
									:rules="[rules.notesMax]"
									density="compact"
									data-cy="quote-notes"
								/>
							</v-col>
						</v-row>
					</v-form>
				</template>
			</v-card-text>

			<v-divider />

			<v-card-actions class="pa-3">
				<v-spacer />
				<v-btn
					color="grey"
					variant="outlined"
					size="small"
					@click="close"
					:disabled="loading"
					data-cy="cancel-quote-generate-btn"
				>
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					variant="flat"
					size="small"
					:loading="loading"
					:disabled="!valid || !trial"
					@click="submit"
					data-cy="submit-quote-generate-btn"
				>
					Generate Quote
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import type { TrialMembership } from "@/stores/support/trials";

const props = defineProps<{
  modelValue: boolean;
  trial: TrialMembership | null;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "submit", payload: {
    trial_id: string;
    duration: number;
    blackbaud: boolean;
    discount?: number;
    notes: string;
  }): void;
}>();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const valid = ref(true);

const form = ref({
  duration: 1,
  blackbaud: false,
  discount: undefined as number | undefined,
  notes: ""
});

const trial = computed(() => props.trial);
const loading = computed(() => props.loading ?? false);

const rules = {
  required: (v: unknown) => v != null && v !== "" || "Required",
  minDuration: (v: number) => (typeof v === "number" && v >= 1) || "Must be at least 1 year",
  discountNonNegative: (v: unknown) =>
    v == null || v === "" || (typeof v === "number" && !Number.isNaN(v) && v >= 0) || "Must be 0 or more",
  notesMax: (v: string) => !v || v.length <= 500 || "Max 500 characters"
};

watch(
  () => props.trial,
  (t) => {
    if (t) {
      form.value = { duration: 1, blackbaud: false, discount: undefined, notes: "" };
    }
  },
  { immediate: true }
);

const close = () => {
  emit("update:modelValue", false);
  setTimeout(() => {
    form.value = { duration: 1, blackbaud: false, discount: undefined, notes: "" };
    formRef.value?.validate?.();
  }, 300);
};

const submit = async () => {
  if (!trial.value) return;
  const result = formRef.value ? await formRef.value.validate() : { valid: false };
  if (!result.valid) return;
  const discountVal = form.value.discount;
  emit("submit", {
    trial_id: trial.value.trial_id,
    duration: Math.max(1, Number(form.value.duration) || 1),
    blackbaud: !!form.value.blackbaud,
    ...(discountVal != null && !Number.isNaN(Number(discountVal))
      ? { discount: Math.max(0, Number(discountVal)) }
      : {}),
    notes: (form.value.notes ?? "").trim().slice(0, 500)
  });
};
</script>

<style scoped lang="scss">
.quote-generate-modal {
  .v-card-title {
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
  }

  .v-card-actions {
    background-color: #f5f5f5;
    border-top: 1px solid #e0e0e0;
  }
}
</style>
